import { Icon } from '../../../components/icons/Icon'
import { StatusPill } from '../../../components/StatusPill'
import type { ColumnDef } from '../../../design-system/list-view/types'
import type { UserView } from '../../../types/userAdmin'

/**
 * User-admin-entra-federation Slice B — Users is a tenant-wide roster, not a cross-case list, so
 * the CLAUDE.md §4 "Account · Legal entity · Case · Fiscal year" column rule does not mechanically
 * apply here (design §2.2's own note — no User Admin prototype exists yet to ratify the exact set,
 * so this is the smallest honest column set: name · email · profile · status · invited).
 */
export function buildUserColumns(): ColumnDef<UserView>[] {
  return [
    {
      id: 'name',
      header: 'Name',
      width: 220,
      minWidth: 160,
      // No server-side sort param exists for UserApi.list (design §2.2 — cursor/limit only, newest-
      // first fixed order) — `sortable` stays false everywhere here so the sort menu never offers a
      // control that would silently no-op (CLAUDE.md §4 "every CTA must open a real … action").
      sortable: false,
      pinnable: true,
      toExportText: (row) => row.name,
      render: (row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon name="users" size={14} style={{ color: 'var(--iw-accent)' }} />
          {row.name}
        </span>
      ),
    },
    {
      id: 'email',
      header: 'Email',
      width: 240,
      sortable: false,
      toExportText: (row) => row.email,
      render: (row) => row.email,
    },
    {
      id: 'profileKey',
      header: 'Profile',
      width: 160,
      sortable: false,
      toExportText: (row) => row.profileKey ?? '',
      render: (row) => row.profileKey ?? <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>,
    },
    {
      id: 'status',
      header: 'Status',
      width: 130,
      sortable: false,
      toExportText: (row) => row.status,
      render: (row) =>
        row.status === 'active' ? (
          <StatusPill variant="success" label="Active" icon="user-check" />
        ) : (
          <StatusPill variant="muted" label="Deactivated" icon="user-x" />
        ),
    },
    {
      id: 'createdAt',
      header: 'Invited',
      width: 160,
      sortable: false,
      toExportText: (row) => row.createdAt,
      render: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
  ]
}

export const USER_COLUMN_ORDER = ['name', 'email', 'profileKey', 'status', 'createdAt']
